import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/integrations/supabase/AuthProvider';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Chrome, MessagesSquare, ShieldCheck, Zap, Users } from "lucide-react";
import logo from "@/assets/logo.png";

const HERO_IMAGE = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=1964";

function Login() {
  const { session } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  
  const referralCode = searchParams.get('ref');

  useEffect(() => {
    if (referralCode) {
      localStorage.setItem('sonas_ref_code', referralCode);
      if (!isSignUp) setIsSignUp(true);
    }
  }, [referralCode]);

  if (session) {
    return <Navigate to="/app" replace />;
  }

  const handleAuth = async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setLoading(true);
    const authId = toast.loading(isSignUp ? "Forging your identity..." : "Authenticating node...");

    try {
      if (isSignUp) {
        if (!fullName.trim()) throw new Error("Full name is required for registration.");
        
        const storedRef = localStorage.getItem('sonas_ref_code');
        
        const { data, error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            data: { 
              full_name: fullName.trim(),
              referred_by_code: storedRef || null
            }
          }
        });

        if (error) throw error;
        
        if (data?.user && data?.session) {
          toast.success("Identity initialized. Welcome to the Forge.", { id: authId });
          localStorage.removeItem('sonas_ref_code');
        } else {
          toast.success("Node created. Please verify your email to activate.", { id: authId });
          setIsSignUp(false);
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ 
          email, 
          password
        });
        if (error) throw error;
        toast.success("Access granted. Welcome back, Architect.", { id: authId });
      }
    } catch (error) {
      toast.error(error.message, { id: authId });
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider) => {
    const storedRef = localStorage.getItem('sonas_ref_code');
    try {
      const { error } = await supabase.auth.signInWithOAuth({ 
        provider,
        options: {
          redirectTo: `${window.location.origin}/app`,
          queryParams: storedRef ? { ref: storedRef } : {}
        }
      });
      if (error) throw error;
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="flex min-h-screen bg-black text-white font-sans selection:bg-sonas-accent/30">
      <div className="w-full lg:w-[45%] flex flex-col p-8 lg:p-16 relative overflow-y-auto border-r border-white/5 bg-black">
        <Link to="/" className="mb-12">
          <img src={logo} alt="Sonas" className="h-8 w-auto brightness-0 invert" />
        </Link>

        <div className="max-w-md mx-auto w-full flex-1 flex flex-col justify-center">
          <div className="space-y-3 mb-10">
            {referralCode && (
              <div className="flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full w-fit animate-pulse">
                <Users className="h-3 w-3 text-blue-400" />
                <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Referral Link Active</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-1 bg-sonas-accent/10 border border-sonas-accent/20 rounded-full w-fit">
              <ShieldCheck className="h-3 w-3 text-sonas-accent" />
              <span className="text-[10px] font-bold text-sonas-accent uppercase tracking-widest">Secure Entry Node</span>
            </div>
            <h1 className="text-4xl font-syne font-bold tracking-tight">
              {isSignUp ? "Join the Forge" : "Welcome back"}
            </h1>
            <p className="text-sonas-dim text-sm">
              {isSignUp ? "Start your architectural journey today." : "Access your studio workspaces."}
            </p>
          </div>

          <form onSubmit={handleAuth} className="space-y-5">
            {isSignUp && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                <label className="text-[10px] font-bold text-sonas-dim uppercase tracking-[0.2em] ml-1">Full Name</label>
                <Input 
                  type="text" 
                  placeholder="John Architect" 
                  className="h-14 bg-white/[0.03] border-white/5 rounded-2xl focus:ring-sonas-accent text-base"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required={isSignUp}
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-sonas-dim uppercase tracking-[0.2em] ml-1">Architect Identity (Email)</label>
              <Input 
                type="email" 
                placeholder="architect@sonas.io" 
                className="h-14 bg-white/[0.03] border-white/5 rounded-2xl focus:ring-sonas-accent text-base"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center px-1">
                <label className="text-[10px] font-bold text-sonas-dim uppercase tracking-[0.2em]">Password</label>
                {!isSignUp && (
                  <button type="button" className="text-[10px] font-bold text-sonas-accent uppercase hover:underline">Reset</button>
                )}
              </div>
              <Input 
                type="password" 
                placeholder="••••••••" 
                className="h-14 bg-white/[0.03] border-white/5 rounded-2xl focus:ring-sonas-accent text-base"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-14 rounded-2xl bg-sonas-accent hover:bg-sonas-accent/90 text-sonas-bg font-bold text-lg mt-2 transition-all shadow-xl"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (isSignUp ? "Initialize Account" : "Access Workspace")}
            </Button>
          </form>

          <div className="relative my-10">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/5"></div></div>
            <div className="relative flex justify-center text-[10px] uppercase font-bold tracking-widest"><span className="bg-black px-4 text-sonas-dim">Social Credentials</span></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Button variant="outline" onClick={() => handleOAuth('google')} className="h-14 bg-white/[0.03] border-white/5 rounded-2xl font-bold gap-3"><Chrome className="h-4 w-4 text-red-400" /> Google</Button>
            <Button variant="outline" onClick={() => handleOAuth('discord')} className="h-14 bg-white/[0.03] border-white/5 rounded-2xl font-bold gap-3"><MessagesSquare className="h-4 w-4 text-indigo-400" /> Discord</Button>
          </div>

          <div className="mt-12 text-center text-sm">
            <span className="text-sonas-dim font-medium">{isSignUp ? "Already an architect?" : "New to the workspace?"}</span>{" "}
            <button onClick={() => setIsSignUp(!isSignUp)} className="text-sonas-accent hover:underline font-bold">{isSignUp ? "Log in" : "Create Account"}</button>
          </div>
        </div>
      </div>

      <div className="hidden lg:block flex-1 relative overflow-hidden bg-black">
        <img src={HERO_IMAGE} className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent z-10 w-full" />
        <div className="absolute bottom-20 left-20 z-20 max-w-lg space-y-6">
          <div className="h-16 w-16 rounded-3xl bg-sonas-accent/10 flex items-center justify-center border border-sonas-accent/20 backdrop-blur-xl">
             <Zap className="h-8 w-8 text-sonas-accent" />
          </div>
          <h2 className="text-5xl font-syne font-bold leading-tight">The smart way <br /> to build Roblox.</h2>
          <p className="text-sonas-dim text-lg">Join thousands of architects building high-performance systems with the help of specialized AI.</p>
        </div>
      </div>
    </div>
  );
}

export default Login;